/**
 * Typed client for the bakery API.
 *
 * One base URL for both targets: the web build talks to a same-origin `/api`
 * through the Vite proxy or the reverse proxy in production, while the Android
 * build is handed the shop server's LAN address at startup. Nothing else in the
 * app needs to know which of the two it is running in.
 */

import type {
  AuthenticatedUser,
  LoginResult,
  Permission,
  PermissionGroup,
  UserRole,
  AppSettings,
  CannedReply,
  Category,
  Channel,
  ChannelType,
  Conversation,
  ConversationWithMessages,
  Customer,
  CustomerProfile,
  DashboardSummary,
  Discount,
  Employee,
  ForecastResult,
  Ingredient,
  Message,
  MessageTemplate,
  OpenTicket,
  OpenTicketWithLines,
  OrderStatus,
  OrderWithDetail,
  PayItem,
  Payslip,
  PayslipWithDetail,
  ProductWithDetail,
  Role,
  SalesPoint,
  StockAlert,
  StockShortfall,
  Supplier,
  UnitDef,
  WorkLog,
  WorkShift,
} from '@bakery/shared';

import { remoteOnly, route } from './transport.js';

/** Overridable so the Android build can point at the shop's machine. */
export function apiBase(): string {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('bakery.apiBase') : null;
  return stored ?? import.meta.env.VITE_API_BASE ?? '/api';
}

export function setApiBase(url: string): void {
  localStorage.setItem('bakery.apiBase', url.replace(/\/$/, ''));
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
    /** Machine-readable reason, e.g. PASSWORD_CHANGE_REQUIRED or FORBIDDEN. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// --- Session token ---------------------------------------------------------

const TOKEN_KEY = 'bakery.token';

export function authToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private windows and locked-down webviews can throw on storage access.
    return null;
  }
}

export function setAuthToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing useful to do; the session simply will not persist a reload */
  }
}

/**
 * Called when the server rejects our token. The auth provider registers a
 * handler that clears local state and shows the sign-in screen, so an expired
 * or revoked session does not leave the app stuck on a spinner.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

/**
 * How long to wait before deciding the server is not there.
 *
 * Without a ceiling a request to an address that does not answer simply hangs,
 * and the app shows nothing at all — no error, no fallback, a spinner that
 * never resolves. That is precisely what the Android build did when it was
 * pointed at its own localhost: sign-in "did nothing", because nothing ever
 * failed. Generous enough for a slow shop wifi, short enough that a wrong
 * address is obvious.
 */
const REQUEST_TIMEOUT_MS = 12_000;

async function request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const token = authToken();

  const response = await fetch(`${apiBase()}${path}`, {
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    ...rest,
    // Declaring JSON with no body makes Fastify reject the request outright.
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...rest.headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    const detail = payload as { error?: string; code?: string } | null;
    const message = detail?.error ?? `Request failed (${response.status})`;

    // A dead session should drop straight to the login screen rather than
    // surfacing as a generic error on whatever screen happened to be open.
    if (response.status === 401 && path !== '/auth/login') {
      setAuthToken(null);
      onUnauthorized();
    }

    throw new ApiError(message, response.status, payload, detail?.code);
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const get = <T,>(path: string) => request<T>(path);
const post = <T,>(path: string, json?: unknown) => request<T>(path, { method: 'POST', json: json ?? {} });
const patch = <T,>(path: string, json: unknown) => request<T>(path, { method: 'PATCH', json });
const put = <T,>(path: string, json: unknown) => request<T>(path, { method: 'PUT', json });
const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' });

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

// --- Shapes the server adds on top of the shared types ---------------------

export interface IngredientRow extends Ingredient {
  qtyDisplay: number;
  reorderPointDisplay: number;
  costPerStockUnit: number;
}

export interface StockAlertRow extends StockAlert {
  qtyDisplay: number;
  suggestedOrderDisplay: number;
  dailyBurnDisplay: number;
}

/** A shortfall with the base-unit quantities converted into readable ones. */
export interface StockShortfallRow extends StockShortfall {
  onHandDisplay: number;
  requiredDisplay: number;
  shortByDisplay: number;
}

export interface AvailabilityReportRow {
  shortfalls: StockShortfallRow[];
  ok: boolean;
  hasRecordDrift: boolean;
}

export interface NotifyResult {
  attempted: boolean;
  status: 'sent' | 'queued' | 'skipped' | 'failed';
  channel: ChannelType | null;
  body: string | null;
  error: string | null;
  handoffUrl: string | null;
}

export interface ReceiptPayload {
  receiptNo: string;
  orderNo: string;
  text: string;
  escpos: string;
  paperWidthMm: number;
  lines: { name: string; qty: number; unitPrice: string; lineTotal: string; modifiers: string[]; notes: string | null }[];
  totals: { label: string; value: string; emphasis?: boolean }[];
  payments: { method: string; amount: string; reference: string | null }[];
  changeDue: string | null;
  customer: string | null;
  cashier: string | null;
  business: AppSettings['business'];
  footerLines: string[];
}

export interface ChannelCapabilityRow {
  type: ChannelType;
  label: string;
  canSendDirectly: boolean;
  transport: Channel['transport'];
  requiredCredentials: { key: string; label: string; hint: string }[];
  limitation: string | null;
  channel: Channel | null;
}

export interface DashboardPayload {
  range: { from: string; to: string };
  summary: DashboardSummary;
  series: SalesPoint[];
  topProducts: { productId: string; productName: string; qtySold: number; net: number; profit: number; marginPercent: number; salesShare: number }[];
  channels: { source: string; orderCount: number; net: number; averageOrderValue: number }[];
  orderBuckets: Record<string, number>;
  stockAlerts: StockAlertRow[];
}

export interface PayslipDraftResponse {
  draft: {
    lines: { payItemId: string | null; label: string; unitLabel: string; qty: number; rate: number; amount: number; breakdown: { date: string; qty: number }[] }[];
    adjustments: { label: string; amount: number; kind: 'addition' | 'deduction' }[];
    subtotal: number;
    additions: number;
    deductions: number;
    grandTotal: number;
  };
  employee: Employee;
  business: AppSettings['business'];
}

// --- The client ------------------------------------------------------------

export const api = {
  health: () => get<{ ok: boolean; database: string; time: string }>('/health'),

  auth: {
    /*
     * Wrapped so an unreachable server surfaces as OfflineUnavailableError
     * rather than a bare 503. The sign-in flow falls back to this device only
     * for that specific case: a *refusal* from the server is an answer and must
     * stand, or a disabled account could be worked around by pulling the cable.
     */
    login: (identifier: string, password: string) =>
      remoteOnly('Signing in', () => post<LoginResult>('/auth/login', { identifier, password })),
    logout: () => post<{ ok: true }>('/auth/logout'),
    logoutEverywhere: () => post<{ ok: true }>('/auth/logout-everywhere'),
    me: () => get<AuthenticatedUser>('/auth/me'),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: true; user: AuthenticatedUser; token: string; expiresAt: string }>('/auth/change-password', {
        currentPassword,
        newPassword,
      }),
    permissionCatalogue: () =>
      get<{ groups: PermissionGroup[]; all: Permission[]; staffDefaults: Permission[] }>('/auth/permissions'),
  },

  users: {
    list: () => get<(AuthenticatedUser & { employeeName: string | null })[]>('/users'),
    create: (body: {
      email: string;
      loginId: string;
      displayName: string;
      password: string;
      role?: UserRole;
      permissions?: Permission[];
      employeeId?: string | null;
    }) => post<AuthenticatedUser>('/users', body),
    update: (
      id: string,
      body: Partial<{
        email: string;
        loginId: string;
        displayName: string;
        role: UserRole;
        permissions: Permission[];
        employeeId: string | null;
        isActive: boolean;
      }>,
    ) => patch<AuthenticatedUser>(`/users/${id}`, body),
    resetPassword: (id: string, password: string) =>
      post<{ ok: true; mustChangePassword: boolean }>(`/users/${id}/password`, { password }),
    remove: (id: string) => del<{ ok: true }>(`/users/${id}`),
  },

  settings: {
    get: () =>
      route<AppSettings>({
        label: 'Loading settings',
        remote: () => get<AppSettings>('/settings'),
        local: { name: 'settings.get' },
      }),
    update: (patchBody: Partial<AppSettings>) => patch<AppSettings>('/settings', patchBody),
  },

  templates: {
    list: () => get<{ templates: MessageTemplate[]; variables: { path: string; label: string; example: string }[] }>('/templates'),
    save: (body: { key: string; name: string; body: string }) => post<MessageTemplate>('/templates', body),
    update: (id: string, body: Partial<MessageTemplate>) => patch<MessageTemplate>(`/templates/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/templates/${id}`),
    preview: (body: string) => post<{ preview: string }>('/templates/preview', { body }),
    reset: (key: string) => post<MessageTemplate>(`/templates/${key}/reset`),
  },

  catalog: {
    /*
     * Routed calls fall back to this device's own database when the server
     * cannot be reached. Only reads and writes a device can honestly serve are
     * routed; the rest stay remote-only and say so. See lib/transport.ts.
     */
    categories: () =>
      route<Category[]>({
        label: 'Loading categories',
        remote: () => get<Category[]>('/categories'),
        local: { name: 'catalog.categories' },
      }),
    createCategory: (body: Partial<Category>) => post<Category>('/categories', body),
    updateCategory: (id: string, body: Partial<Category>) => patch<Category>(`/categories/${id}`, body),
    deleteCategory: (id: string) => del<{ ok: true }>(`/categories/${id}`),

    products: (params: { categoryId?: string; search?: string } = {}) =>
      route<ProductWithDetail[]>({
        label: 'Loading the menu',
        remote: () => get<ProductWithDetail[]>(`/products/detailed${query(params)}`),
        local: { name: 'catalog.products', args: params },
      }),
    product: (id: string) => get<ProductWithDetail>(`/products/${id}`),
    createProduct: (body: Record<string, unknown>) => post<ProductWithDetail>('/products', body),
    updateProduct: (id: string, body: Record<string, unknown>) => patch<ProductWithDetail>(`/products/${id}`, body),
    deleteProduct: (id: string) => del<{ ok: true; action: string }>(`/products/${id}`),

    recipe: (productId: string) =>
      get<{ productId: string; batchYield: number; lines: unknown[]; cost: { costPerBatch: number; costPerUnit: number; lines: unknown[]; isComplete: boolean } }>(
        `/products/${productId}/recipe`,
      ),
    saveRecipe: (productId: string, lines: unknown[]) => put(`/products/${productId}/recipe`, { lines }),

    discounts: (includeInactive = false) =>
      route<Discount[]>({
        label: 'Loading discounts',
        remote: () => get<Discount[]>(`/discounts${includeInactive ? '?includeInactive=true' : ''}`),
        local: { name: 'catalog.discounts', args: { includeInactive } },
      }),

    /*
     * Server-only, like the order statuses beside them. Shop configuration is
     * edited in one place by one administrator; letting six registers each
     * invent a discount offline and reconcile later is a merge problem with no
     * good answer, and no cashier is blocked by its absence.
     */
    createDiscount: (body: Partial<Discount>) => post<Discount>('/discounts', body),
    updateDiscount: (id: string, body: Partial<Discount>) => patch<Discount>(`/discounts/${id}`, body),
    deleteDiscount: (id: string) => del<{ ok: true; action: 'deleted' | 'retired' }>(`/discounts/${id}`),
  },

  customers: {
    list: (params: { search?: string; sort?: string; limit?: number; offset?: number } = {}) =>
      route<{ customers: Customer[]; total: number }>({
        label: 'Looking up customers',
        remote: () => get<{ customers: Customer[]; total: number }>(`/customers${query(params)}`),
        // The local repository returns a bare list; shape it like the endpoint.
        local: { name: 'customers.list', args: params },
      }),
    get: (id: string) =>
      get<CustomerProfile & { contactLinks: { channel: ChannelType; handle: string; url: string }[] }>(`/customers/${id}`),
    orders: (id: string) => get<OrderWithDetail[]>(`/customers/${id}/orders`),
    create: (body: Record<string, unknown>) => post<CustomerProfile>('/customers', body),
    update: (id: string, body: Record<string, unknown>) => patch<CustomerProfile>(`/customers/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/customers/${id}`),
    /** Folds `fromId` into `id`; the client in the path is the one that survives. */
    merge: (id: string, fromId: string) => post<CustomerProfile>(`/customers/${id}/merge`, { fromId }),
    addHandle: (id: string, body: { channel: ChannelType; handle: string }) => post(`/customers/${id}/handles`, body),
    removeHandle: (handleId: string) => del(`/handles/${handleId}`),
    addAddress: (id: string, body: Record<string, unknown>) => post(`/customers/${id}/addresses`, body),
    removeAddress: (addressId: string) => del(`/addresses/${addressId}`),
  },

  orders: {
    statuses: (includeInactive = false) =>
      route<OrderStatus[]>({
        label: 'Loading order statuses',
        remote: () => get<OrderStatus[]>(`/order-statuses${query({ includeInactive })}`),
        local: { name: 'orders.statuses', args: { includeInactive } },
      }),
    createStatus: (body: Partial<OrderStatus>) => post<OrderStatus>('/order-statuses', body),
    updateStatus: (id: string, body: Partial<OrderStatus>) => patch<OrderStatus>(`/order-statuses/${id}`, body),
    deleteStatus: (id: string) => del<{ ok: true; action: string }>(`/order-statuses/${id}`),
    reorderStatuses: (ids: string[]) => post<OrderStatus[]>('/order-statuses/reorder', { ids }),

    list: (params: Record<string, string | number | boolean | undefined> = {}) =>
      route<OrderWithDetail[]>({
        label: 'Loading orders',
        remote: () => get<OrderWithDetail[]>(`/orders${query(params)}`),
        local: { name: 'orders.list', args: params },
      }),
    buckets: () => get<{ bucket: string; label: string; count: number }[]>('/orders/buckets'),
    get: (id: string) => get<OrderWithDetail>(`/orders/${id}`),
    create: (body: Record<string, unknown>) =>
      route<{
        order: OrderWithDetail;
        notification: NotifyResult | null;
        availability?: AvailabilityReportRow;
        /* Present when the sale was rung up on this device: the local path
         * numbers the receipt as part of the sale, because there is no server
         * to ask for one afterwards. */
        receipt?: ReceiptPayload;
      }>({
        label: 'Ringing up a sale',
        remote: () =>
          post<{ order: OrderWithDetail; notification: NotifyResult | null; availability?: AvailabilityReportRow }>(
            '/orders',
            body,
          ),
        // A sale rung up offline is a real sale: written to this device's own
        // database and pushed to the others when the connection returns.
        local: { name: 'orders.create', args: body },
      }),
    update: (id: string, body: Record<string, unknown>) => patch<OrderWithDetail>(`/orders/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/orders/${id}`),

    changeStatus: (id: string, body: { statusId: string; note?: string; notify?: boolean; bodyOverride?: string; employeeId?: string }) =>
      post<{ order: OrderWithDetail; notification: NotifyResult | null }>(`/orders/${id}/status`, body),
    previewMessage: (id: string, statusId: string) =>
      get<{ statusName: string; hasTemplate: boolean; preview: string | null }>(`/orders/${id}/preview/${statusId}`),
    addPayment: (id: string, body: Record<string, unknown>) =>
      post<{ order: OrderWithDetail; notification: NotifyResult | null }>(`/orders/${id}/payments`, body),

    receipt: (id: string) => get<ReceiptPayload>(`/orders/${id}/receipt`),
    markPrinted: (id: string) => post<ReceiptPayload>(`/orders/${id}/receipt/printed`),
  },

  sync: {
    /** Reference data a device needs to work on its own. */
    snapshot: () =>
      get<{ fromDeviceId: string; takenAt: string; tables: { table: string; rows: Record<string, unknown>[] }[] }>(
        '/sync/snapshot',
      ),

    /*
     * Not routed through `route()`. These two *are* the connection: falling back
     * to the local database for them would mean syncing with ourselves, which
     * is a fast way to advance a cursor past changes nobody ever received.
     */
    pull: (params: { since: string | null; deviceId: string }) =>
      get<{ fromDeviceId: string; cursor: string | null; changes: unknown[]; hasMore: boolean }>(
        `/sync/pull${query({ since: params.since ?? undefined, deviceId: params.deviceId })}`,
      ),

    push: (body: { fromDeviceId: string; changes: unknown[]; cursor: string | null }) =>
      post<{ applied: number; superseded: number; rejected: number; hubDeviceId: string }>('/sync/push', body),
  },

  tickets: {
    list: () =>
      route<{ tickets: OpenTicket[]; count: number }>({
        label: 'Loading parked tickets',
        remote: () => get<{ tickets: OpenTicket[]; count: number }>('/tickets'),
        local: { name: 'tickets.list' },
      }),
    get: (id: string) =>
      route<OpenTicketWithLines>({
        label: 'Opening a parked ticket',
        remote: () => get<OpenTicketWithLines>(`/tickets/${id}`),
        local: { name: 'tickets.get', args: { id } },
      }),
    park: (body: { name: string; lines: unknown[]; customerId?: string | null; customerName?: string | null; note?: string | null; employeeId?: string | null }) =>
      route<OpenTicketWithLines>({
        label: 'Parking a sale',
        remote: () => post<OpenTicketWithLines>('/tickets', body),
        local: { name: 'tickets.park', args: body },
      }),
    update: (id: string, body: Record<string, unknown>) =>
      route<OpenTicketWithLines>({
        label: 'Updating a parked ticket',
        remote: () => patch<OpenTicketWithLines>(`/tickets/${id}`, body),
        local: { name: 'tickets.update', args: { ...body, id } },
      }),
    discard: (id: string) =>
      route<{ ok: true }>({
        label: 'Discarding a ticket',
        remote: () => del<{ ok: true }>(`/tickets/${id}`),
        local: { name: 'tickets.discard', args: { id } },
      }),
    checkout: (id: string, body: { method: string; tendered?: number | null; reference?: string | null; discountId?: string | null }) =>
      route<{ order: OrderWithDetail; receipt: ReceiptPayload }>({
        label: 'Charging a parked sale',
        remote: () => post<{ order: OrderWithDetail; receipt: ReceiptPayload }>(`/tickets/${id}/checkout`, body),
        local: { name: 'tickets.checkout', args: { ...body, id } },
      }),
  },

  inventory: {
    units: () => get<{ all: UnitDef[]; byDimension: Record<string, UnitDef[]> }>('/units'),
    ingredients: () => get<IngredientRow[]>('/ingredients'),
    ingredient: (id: string) =>
      get<IngredientRow & { movements: unknown[]; usedIn: { id: string; name: string }[] }>(`/ingredients/${id}`),
    createIngredient: (body: Record<string, unknown>) => post<Ingredient>('/ingredients', body),
    updateIngredient: (id: string, body: Record<string, unknown>) => patch<Ingredient>(`/ingredients/${id}`, body),
    /** Refused with a 409 and an explanation when a recipe or delivery holds it. */
    deleteIngredient: (id: string) => del<{ ok: true; movementsRemoved: number }>(`/ingredients/${id}`),
    move: (id: string, body: { qty: number; unit?: string; type?: string; note?: string }) =>
      post(`/ingredients/${id}/movements`, body),
    count: (id: string, body: { countedQty: number; unit?: string; note?: string }) =>
      post(`/ingredients/${id}/count`, body),

    alerts: (includeOk = false) => get<StockAlertRow[]>(`/stock-alerts${query({ includeOk })}`),

    /** Would this cart take anything below zero? Reports; never refuses. */
    check: (lines: { productId: string | null; name: string; qty: number }[]) =>
      route<AvailabilityReportRow>({
        label: 'Checking stock',
        remote: () => post<AvailabilityReportRow>('/stock/check', { lines }),
        local: { name: 'stock.check', args: { lines } },
      }),

    suppliers: () => get<Supplier[]>('/suppliers'),
    createSupplier: (body: Record<string, unknown>) => post<Supplier>('/suppliers', body),
    purchases: () => get<unknown[]>('/purchases'),
    receive: (body: Record<string, unknown>) => post('/purchases', body),
  },

  staff: {
    roles: () => get<Role[]>('/roles'),
    employees: (includeInactive = false) =>
      get<(Employee & { payItems: PayItem[] })[]>(`/employees${query({ includeInactive })}`),
    employee: (id: string) =>
      get<Employee & { payItems: PayItem[]; recentPayslips: Payslip[] }>(`/employees/${id}`),
    create: (body: Record<string, unknown>) => post<Employee>('/employees', body),
    update: (id: string, body: Record<string, unknown>) => patch<Employee>(`/employees/${id}`, body),
    remove: (id: string) => del<{ ok: true; action: string }>(`/employees/${id}`),

    savePayItem: (employeeId: string, body: Record<string, unknown>) => post<PayItem>(`/employees/${employeeId}/pay-items`, body),
    deletePayItem: (payItemId: string) => del(`/pay-items/${payItemId}`),

    workLogs: (employeeId: string, from: string, to: string) =>
      get<WorkLog[]>(`/employees/${employeeId}/work-logs${query({ from, to })}`),
    saveWorkLogs: (employeeId: string, entries: { payItemId: string; workDate: string; qty: number }[]) =>
      put<WorkLog[]>(`/employees/${employeeId}/work-logs`, { entries }),
    autoFill: (employeeId: string, from: string, to: string) =>
      post<WorkLog[]>(`/employees/${employeeId}/work-logs/auto-fill`, { from, to }),

    shifts: (params: { from?: string; to?: string; employeeId?: string } = {}) =>
      get<WorkShift[]>(`/work-shifts${query(params)}`),
    createShift: (body: Record<string, unknown>) => post<WorkShift>('/work-shifts', body),
    updateShift: (id: string, body: Record<string, unknown>) => patch<WorkShift>(`/work-shifts/${id}`, body),
    deleteShift: (id: string) => del(`/work-shifts/${id}`),

    draftPayslip: (body: { employeeId: string; periodStart: string; periodEnd: string; adjustments?: unknown[] }) =>
      post<PayslipDraftResponse>('/payslips/draft', body),
    issuePayslip: (body: Record<string, unknown>) => post<PayslipWithDetail>('/payslips', body),
    payslips: (employeeId?: string) => get<Payslip[]>(`/payslips${query({ employeeId })}`),
    payslip: (id: string) => get<PayslipWithDetail & { business: AppSettings['business'] }>(`/payslips/${id}`),
    markPaid: (id: string, method: string) => post<Payslip>(`/payslips/${id}/paid`, { method }),
  },

  inbox: {
    channels: () => get<ChannelCapabilityRow[]>('/channels'),
    connectChannel: (body: { type: ChannelType; name: string; credentials?: Record<string, string> }) =>
      post<Channel>('/channels', body),
    disconnectChannel: (type: ChannelType) => del(`/channels/${type}`),

    conversations: (params: Record<string, string | boolean | undefined> = {}) =>
      get<{ conversations: Conversation[]; counts: Record<string, number> }>(`/conversations${query(params)}`),
    conversation: (id: string) =>
      get<ConversationWithMessages & { customer: CustomerProfile | null; linkedOrder: OrderWithDetail | null; handoffUrl: string | null }>(
        `/conversations/${id}`,
      ),
    update: (id: string, body: Record<string, unknown>) => patch<Conversation>(`/conversations/${id}`, body),
    reply: (id: string, body: { body: string; authorId?: string; authorName?: string; isPrivateNote?: boolean }) =>
      post<{ message: Message; delivered: boolean; error: string | null; handoffUrl: string | null }>(
        `/conversations/${id}/messages`,
        body,
      ),
    /** Fill in anything Messenger never delivered to our webhook. */
    syncConversation: (id: string) =>
      post<{ added: number; examined: number; unreadable: boolean }>(`/conversations/${id}/sync`, {}),
    /**
     * Send a picture or a file.
     *
     * Base64 rather than multipart, because the server already parses JSON and
     * nothing about a 3 MB photograph justifies a second body parser.
     */
    sendAttachment: (id: string, body: { name: string; mimeType: string; data: string; caption?: string }) =>
      post<{ message: Message; delivered: boolean; error: string | null; handoffUrl?: string | null }>(
        `/conversations/${id}/attachments`,
        body,
      ),
    linkCustomer: (id: string, customerId?: string) =>
      post<{ customerId: string; customer: CustomerProfile }>(`/conversations/${id}/link-customer`, { customerId }),
    createOrder: (id: string, body: Record<string, unknown>) =>
      post<{ order: OrderWithDetail; notification: NotifyResult | null }>(`/conversations/${id}/order`, body),

    cannedReplies: () => get<CannedReply[]>('/canned-replies'),
    createCannedReply: (body: { title: string; body: string; shortcut?: string }) => post<CannedReply>('/canned-replies', body),
    deleteCannedReply: (id: string) => del(`/canned-replies/${id}`),
  },

  analytics: {
    dashboard: (period = 'today') => get<DashboardPayload>(`/analytics/dashboard${query({ period })}`),
    summary: (params: Record<string, string | undefined>) => get<DashboardSummary>(`/analytics/summary${query(params)}`),
    series: (params: Record<string, string | undefined>) => get<SalesPoint[]>(`/analytics/series${query(params)}`),
    products: (params: Record<string, string | number | undefined>) => get<DashboardPayload['topProducts']>(`/analytics/products${query(params)}`),
    channels: (params: Record<string, string | undefined>) => get<DashboardPayload['channels']>(`/analytics/channels${query(params)}`),
    heatmap: (params: Record<string, string | undefined>) =>
      get<{ weekday: number; hour: number; orderCount: number; net: number }[]>(`/analytics/heatmap${query(params)}`),
    forecast: (days?: number) => get<ForecastResult>(`/analytics/forecast${query({ days })}`),
    ingredientDemand: (days?: number) =>
      get<
        {
          ingredientId: string;
          ingredientName: string;
          stockUnit: string;
          requiredDisplay: number;
          onHandDisplay: number;
          shortfallDisplay: number;
          estimatedCost: number;
        }[]
      >(`/analytics/ingredient-demand${query({ days })}`),
  },
};
